import {
    IAuditChange,
    IAuditExclusionCollection,
    IAuditLog,
    IAuditLogDetail,
    JobState,
    JobType,
} from '@models';
import {
    compareObjects,
    getProperty,
} from '@utils/object.utils';
import {
    isBool,
    isJson,
} from '@utils/string.utils';

import cloneDeep from 'lodash-es/cloneDeep';
import isArray from 'lodash-es/isArray';
import values from 'lodash-es/values';
import * as moment from 'moment';

const propertyExclusions: IAuditExclusionCollection = {
    'Environments': {
        'Update': {
            properties: {
                'UiEnvironment': ['Robots'],
            },
        },
    },
    'Robots': {
        'Create': {
            objects: ['UiSession', 'UiUserOrganizationUnitRole'],
        },
        'Delete': {
            objects: ['UiSession', 'UiUserOrganizationUnitRole'],
        },
    },
    'Users': {
        'All': {
            properties: {
                'UserRole': ['UserName'],
            },
        },
        'Update': {
            properties: {
                'UserOrganizationUnit': ['UserName'],
            },
        },
    },
    'Roles': {
        'Associate': {
            objects: ['UiUser'],
        },
        'All': {
            properties: {
                'UserRole': ['RoleName'],
                'RolePermissionSetting': ['IsGranted'],
            },
        },
    },
    'Jobs': {
        'All': {
            objects: ['JobBatchInfo'],
        },
        'StopJob': {
            properties: {
                'UiJob': ['Key', 'State', 'BatchExecutionKey'],
            },
        },
    },
    'Tenant': {
        'All': {
            properties: {
                'RolePermissionSetting': ['IsGranted'],
            },
        },
    },
    'Units': {
        'Associate': {
            properties: {
                'UserOrganizationUnit': ['OrganizationUnitName'],
            },
        },
    },
};

const idListExceptions = ['TimeZoneId', 'CredentialStoreId', 'OrganizationUnitId'];

const formatStopAfter = (stopAfter: string) => {
    return moment().add(stopAfter, 'seconds').fromNow(true);
};

export const cleanupAssetTypeProperties = (data: any, type = 'All', model: 'Global' | 'Robot' = 'Global') => {
    switch (type) {
        case 'Bool':
            delete data.StringValue;
            delete data.IntValue;
            delete data.CredentialUsername;
            delete data.ExternalName;
            break;
        case 'Text':
            delete data.BoolValue;
            delete data.IntValue;
            delete data.CredentialUsername;
            delete data.ExternalName;
            break;
        case 'Integer':
            delete data.StringValue;
            delete data.BoolValue;
            delete data.CredentialUsername;
            delete data.ExternalName;
            break;
        case 'Credential':
            delete data.StringValue;
            delete data.IntValue;
            delete data.BoolValue;
            break;
        default:
            delete data.StringValue;
            delete data.IntValue;
            delete data.BoolValue;
            delete data.CredentialUsername;
            delete data.ExternalName;
    }
    if (model === 'Robot') {
        delete data.ValueType;
        const robotName = data['RobotName'];
        delete data.RobotName;
        const valueKey = Object.keys(data)[0];
        data[robotName] = data[valueKey];
        delete data[valueKey];
    }
};

const containsValue = (data: any) => {
    return data.hasDefaultValue;
};

export const filterAssetResponse = (entry: IAuditLogDetail, data: any) => {
    if (entry.entityName === 'UiAsset') {
        if (data.Values.ValueScope === 'Global') {
            cleanupAssetTypeProperties(data.Values, data.Values.ValueType);
        } else if (data.Values.ValueScope === 'PerRobot') {
            if (containsValue(data.Values)) {
                cleanupAssetTypeProperties(data.Values, data.Values.ValueType);
            } else {
                cleanupAssetTypeProperties(data.Values);
            }
            for (const robotValue of data.Values.RobotValues) {
                cleanupAssetTypeProperties(robotValue, data.Values.ValueType, 'Robot');
            }
        }
        if (entry.action === 'Update') {
            if (data.OldValues.ValueScope === 'Global') {
                cleanupAssetTypeProperties(data.OldValues, data.OldValues.ValueType);
            } else if (data.OldValues.ValueScope === 'PerRobot') {
                if (containsValue(data.OldValues)) {
                    cleanupAssetTypeProperties(data.OldValues, data.OldValues.ValueType);
                } else {
                    cleanupAssetTypeProperties(data.OldValues);
                }
            }
            for (const robotValue of data.OldValues.RobotValues) {
                cleanupAssetTypeProperties(robotValue, data.OldValues.ValueType, 'Robot');
            }
        }
    }
};

export const flattenDynamicProperties = (entry: any) => {
    if (!entry) { return; }

    for (const key of Object.keys(entry)) {
        if (entry[key].hasOwnProperty('DynamicProperties')) {
            entry[key] = entry[key]['DynamicProperties'];
        }
    }
};

export const flattenSinglePropertyArrays = (entry: any) => {
    if (!entry) { return; }
    for (const key of Object.keys(entry)) {
        if (isArray(entry[key]) && entry[key].length > 0) {
            if (entry.RobotValues && entry.RobotValues.length > 0) {
                // don't flatten robot assets
                continue;
            }
            const keys = Object.keys(entry[key][0]);
            if (keys.length === 1) {
                entry[key] = entry[key].map((item: any) => item[keys[0]]);
            }
        }
    }
};

export const filterIdAndKeyProperties = (object: any) => {
    for (const property in object) {
        if (!object.hasOwnProperty(property)) { continue; }

        if (object[property] instanceof Object) {
            object[property] = filterIdAndKeyProperties(object[property]);
        } else if (
            !idListExceptions.includes(property) &&
            property.endsWith('Id') ||
            property.endsWith('Key')
        ) {
            // the id property should not be displayed
            delete object[property];
        }
    }
    return object;
};

const flattenSettingValue = (settings: any) => {
    if (
        settings &&
        !isBool(settings.Value) &&
        isJson(settings.Value)
    ) {
        settings.Value = JSON.parse(settings.Value);
        delete settings.Value.RegexComplexity;
        Object.assign(settings, settings.Value);
        delete settings.Value;
    }
};

export const mapSettings = (entry: IAuditLogDetail, data: any, settingsMap: any) => {
    if (entry.entityName === 'Setting') {
        if (entry.action === 'Create') {
            data['OldValues'] = cloneDeep(data.Values);
            data.OldValues.Value = '*Host Setting*';
            entry.action = 'Update';
        } else if (entry.action === 'Delete') {
            data['OldValues'] = cloneDeep(data.Values);
            data.Values.Value = '*Host Setting*';
            entry.action = 'Update';
        }
        // rewrite the names
        if (data['Values'] && data['Values']['Name'] && settingsMap[data.Values.Name]) {
            data['Values']['Name'] = settingsMap[data.Values.Name];
            entry.entityName = data['Values']['Name'];
            delete data.Values['Name'];
        }
        if (data['OldValues'] && data['OldValues']['Name'] && settingsMap[data.OldValues.Name]) {
            data['OldValues']['Name'] = settingsMap[data.OldValues.Name];
            entry.entityName = data['OldValues']['Name'];
            delete data.OldValues['Name'];
        }

        flattenSettingValue(data.Values);
        flattenSettingValue(data.OldValues);
    }
};

export const mapJobs = (entry: IAuditLogDetail, jobValues: any) => {
    if (entry.entityName === 'UiJob') {
        const robot = jobValues && getProperty(jobValues, 'Robot');
        if (robot) {
            jobValues['Robot'] = robot['Name'];
        }
        if (jobValues) {
            if (jobValues['Type'] != null) {
                jobValues['Type'] = JobType[jobValues['Type']];
            }
            if (jobValues['State'] != null) {
                jobValues['State'] = JobState[jobValues['State']];
            }
        }

        ['InputArguments', 'OutputArguments'].forEach(key => {
            if (
                jobValues &&
                jobValues[key] != null
            ) {
                try {
                    jobValues[key] = JSON.parse(jobValues[key]);
                } catch {
                    jobValues[key] = jobValues[key];
                }
            }
        });
    }
};

export const mapProcesses = (entry: IAuditLogDetail, processValues: any) => {
    if (entry.entityName === 'UiRelease') {
        const key = 'InputArguments';
        if (
            processValues &&
            processValues[key] != null
        ) {
            try {
                processValues[key] = JSON.parse(processValues[key]);
            } catch {
                processValues[key] = processValues[key];
            }
        }
    }
};

export const mapPackages = (entry: IAuditLogDetail, packageValues: { [x: string]: any; Arguments: any; }) => {
    if (
        entry.entityName !== 'UiPackageVersion' ||
        !packageValues ||
        !packageValues['Arguments']
    ) { return; }

    ['Input', 'Output'].forEach(key => {
        if (packageValues.Arguments[key] != null) {
            try {
                packageValues.Arguments[key] = JSON.parse(packageValues.Arguments[key]);
            } catch {
                packageValues.Arguments[key] = packageValues.Arguments[key];
            }
        }
    });
};

export const mapTriggers = (entry: IAuditLogDetail, triggerValues?: any) => {
    if (entry.entityName !== 'UiProcessSchedule' || !triggerValues) { return; }

    triggerValues['TimeZone'] = triggerValues['TimeZoneId'];
    delete triggerValues['TimeZoneId'];
    delete triggerValues['TimeZoneIana'];

    ['InputArguments', 'OutputArguments'].forEach(key => {
        if (triggerValues[key] != null) {
            try {
                triggerValues[key] = JSON.parse(triggerValues[key]);
            } catch {
                triggerValues[key] = triggerValues[key];
            }
        }
    });

    if (triggerValues['StopProcessExpression'] != null && triggerValues['StopProcessExpression'] !== '') {
        triggerValues['__StopProcessExpression'] = moment
            .localeData()
            .relativeTime(
                triggerValues['StopProcessExpression'],
                true,
                'ss',
                false,
            );
        triggerValues['StopProcessExpression'] = {
            translate: {
                key: 'global_after_time',
                data: {
                    timeDelta: formatStopAfter(triggerValues['StopProcessExpression']),
                },
            },
        };
    }
};

export const excludeFor = (component: string, action: string, logDetail: IAuditLogDetail) => {
    const obj = cloneDeep(logDetail);
    if (
        propertyExclusions[component] &&
        propertyExclusions[component][action]
    ) {
        const excludedProperties = propertyExclusions[component][action];
        if (
            excludedProperties.objects &&
            excludedProperties.objects.includes(obj.entityName)
        ) {
            obj.data.value = {};
            obj.data.oldValue = {};
        }

        if (
            excludedProperties.properties &&
            excludedProperties.properties[obj.entityName]
        ) {
            for (const key of Object.keys(obj.data.value)) {
                if (excludedProperties.properties[obj.entityName].includes(key)) {
                    delete obj.data.value[key];
                }
            }
        }
    }
    return obj;
};

export const differenceMap = (current: Record<string, any>, previous: Record<string, any>, keys: string[]) => {
    const map: any = {};
    for (const key of keys) {
        const currentValue = current[key];
        const oldValue = previous[key];
        map[key] = !compareObjects(currentValue, oldValue);
    }
    return map;
};

export const filterProperties = (current: any, previous: any) => {
    for (const property in current) {
        if (!current.hasOwnProperty(property)) {
            continue;
        }
        const oldValue = current[property];
        if (oldValue == null || (oldValue instanceof Array && oldValue.length === 0)) {
            // if a previously null-like value has been updated, keep property
            const newValue = previous && previous[property];
            const isValueArray = newValue instanceof Array;
            const isArrayPopulated = isValueArray && newValue.length > 0;
            const isPropertyPopulated = !isValueArray && newValue != null && newValue !== '';
            if (isArrayPopulated || isPropertyPopulated) {
                continue;
            }
            delete current[property];
        }
    }

    return {
        current,
        previous,
    };
};

export const groupComponents = (changes: IAuditChange[]) => {
    const deleteIndexes: any[] = [];
    const components: any = {};
    const actionMap = {
        Create: 'Added',
        Delete: 'Removed',
    };
    changes.forEach((component, index) => {
        if (component.isUpdate ||
            component.keys.length !== 1 ||
            !['Create', 'Delete'].includes(component.action)) {
            return;
        }
        components[component.entityName] = components[component.entityName] || {
            entityName: component.entityName,
            Added: [],
            Removed: [],
        };
        // get the object inside and store it
        const value = component.current[component.keys[0]];
        components[component.entityName][(actionMap as any)[component.action]].push(value);
        deleteIndexes.push(index);
    });
    // remove the reference
    const oldLength = changes.length;
    deleteIndexes.forEach(index => changes.splice(index - (oldLength - changes.length), 1));
    for (const key of Object.keys(components)) {
        const component = components[key];
        const change = {
            title: component.entityName,
            keys: [],
            current: {},
            differenceMap: {},
        } as Partial<IAuditChange> as IAuditChange;

        if (component.Added.length) {
            change.keys.push('Added');
            change.current['Added'] = component.Added;
        }
        if (component.Removed.length) {
            change.keys.push('Removed');
            change.current['Removed'] = component.Removed;
        }
        changes.push(change);
    }
    return changes;
};

export const getTitleFor = (log: IAuditLog, entity: IAuditLogDetail) => {
    switch (log.component) {
        case 'Jobs':
            switch (log.action) {
                case 'StopJob':
                    return `Job ${entity.data.value.ReleaseName}`;
            }
            break;
        default:
            return `${entity.action} ${entity.entityName.replace(/^Ui/, '')}`;
    }
};

export const isEmptyObject = (obj: any) => {
    return typeof obj === 'object' && !Object.keys(obj).length;
};

export const extractChanges = (logDetails: IAuditLogDetail[], log: IAuditLog) => {
    const changes: IAuditChange[] = [];
    logDetails.forEach((detail) => {
        const { current, previous } = filterProperties(detail.data.value, detail.data.oldValue);

        const change = {
            action: detail.action,
            entityName: detail.entityName.replace(/^Ui/, ''),
            title: getTitleFor(log, detail),
            differenceMap: {},
            isUpdate: !!detail.data.oldValue && !isEmptyObject(detail.data.oldValue),
            current,
            previous,
            keys: Array.from(new Set([...Object.keys(current), ...Object.keys(previous || {})])),
        } as IAuditChange;

        if (change.isUpdate) {
            change.differenceMap = differenceMap(change.current, change.previous!, change.keys);
        }

        // if the update has no changes, show the current data
        const hasAtLeastOneChange = values(change.differenceMap).reduce((prev, curr) => prev || curr, false);
        if (change.isUpdate && !hasAtLeastOneChange) {
            change.previous = {};
            change.isUpdate = false;
        }

        changes.push(change);
    });

    return groupComponents(changes);
};
